export interface AzureProfile {
  name: string;
  tenantId: string;
  appId: string;
  clientSecret: string;
}

const fieldAliases = {
  name: ["name", "profilename", "displayname"],
  tenantId: ["tenantid", "tenant", "directoryid"],
  appId: ["appid", "clientid", "applicationid", "applicationclientid"],
  clientSecret: ["clientsecret", "secret", "password"]
} as const;

function normalizeKey(value: string): string {
  return value.toLowerCase().replace(/[\s_-]/g, "");
}

function readString(record: Record<string, unknown>, aliases: readonly string[]): string {
  const normalizedAliases = new Set(aliases.map(normalizeKey));

  for (const [key, value] of Object.entries(record)) {
    if (normalizedAliases.has(normalizeKey(key)) && typeof value === "string") {
      return value;
    }
  }

  return "";
}

function normalizeProfile(value: unknown, index: number): AzureProfile {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error(`Profile ${index + 1} is not a valid object.`);
  }

  const record = value as Record<string, unknown>;
  const tenantId = readString(record, fieldAliases.tenantId).trim();
  const appId = readString(record, fieldAliases.appId).trim();
  const clientSecret = readString(record, fieldAliases.clientSecret);
  const name = readString(record, fieldAliases.name).trim() || `Azure profile ${index + 1}`;

  if (!tenantId || !appId || !clientSecret) {
    throw new Error(`Profile "${name}" must include tenantId, appId, and clientSecret.`);
  }

  return { name, tenantId, appId, clientSecret };
}

function detectCsvDelimiter(content: string): "," | ";" {
  const firstLine = content.split(/\r?\n/, 1)[0] || "";
  let commas = 0;
  let semicolons = 0;
  let quoted = false;

  for (let index = 0; index < firstLine.length; index += 1) {
    const character = firstLine[index];

    if (character === "\"") {
      if (quoted && firstLine[index + 1] === "\"") {
        index += 1;
      } else {
        quoted = !quoted;
      }
    } else if (!quoted && character === ",") {
      commas += 1;
    } else if (!quoted && character === ";") {
      semicolons += 1;
    }
  }

  return semicolons > commas ? ";" : ",";
}

function parseCsvRows(content: string, delimiter: "," | ";"): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let value = "";
  let quoted = false;

  for (let index = 0; index < content.length; index += 1) {
    const character = content[index];

    if (character === "\"") {
      if (quoted && content[index + 1] === "\"") {
        value += "\"";
        index += 1;
      } else {
        quoted = !quoted;
      }
      continue;
    }

    if (character === delimiter && !quoted) {
      row.push(value);
      value = "";
      continue;
    }

    if ((character === "\n" || character === "\r") && !quoted) {
      if (character === "\r" && content[index + 1] === "\n") {
        index += 1;
      }

      row.push(value);
      if (row.some((cell) => cell.trim())) {
        rows.push(row);
      }
      row = [];
      value = "";
      continue;
    }

    value += character;
  }

  if (quoted) {
    throw new Error("The CSV file contains an unclosed quoted value.");
  }

  row.push(value);
  if (row.some((cell) => cell.trim())) {
    rows.push(row);
  }

  return rows;
}

function parseJsonProfiles(content: string): AzureProfile[] {
  const parsed = JSON.parse(content) as unknown;
  const values =
    Array.isArray(parsed)
      ? parsed
      : parsed && typeof parsed === "object" && Array.isArray((parsed as { profiles?: unknown }).profiles)
        ? (parsed as { profiles: unknown[] }).profiles
        : [parsed];

  return values.map(normalizeProfile);
}

function parseCsvProfiles(content: string): AzureProfile[] {
  const normalizedContent = content.replace(/^\uFEFF/, "");
  const rows = parseCsvRows(normalizedContent, detectCsvDelimiter(normalizedContent));

  if (rows.length < 2) {
    throw new Error("The CSV file must contain a header and at least one profile.");
  }

  const headers = rows[0];
  return rows.slice(1).map((cells, index) => {
    const record = Object.fromEntries(headers.map((header, columnIndex) => [header, cells[columnIndex] || ""]));
    return normalizeProfile(record, index);
  });
}

export function parseAzureProfiles(content: string, fileName: string): AzureProfile[] {
  const trimmed = content.trim();

  if (!trimmed) {
    throw new Error("The selected file is empty.");
  }

  const profiles = fileName.toLowerCase().endsWith(".json") || trimmed.startsWith("{") || trimmed.startsWith("[")
    ? parseJsonProfiles(trimmed)
    : parseCsvProfiles(content);

  if (profiles.length === 0) {
    throw new Error("The selected file does not contain any profiles.");
  }

  return profiles;
}

export function serializeAzureProfilesJson(profiles: AzureProfile[]): string {
  return JSON.stringify(
    {
      version: 1,
      profiles
    },
    null,
    2
  );
}

function escapeCsvValue(value: string): string {
  return `"${value.replace(/"/g, "\"\"")}"`;
}

export function serializeAzureProfilesCsv(profiles: AzureProfile[]): string {
  const rows = [
    ["name", "tenantId", "appId", "clientSecret"],
    ...profiles.map((profile) => [profile.name, profile.tenantId, profile.appId, profile.clientSecret])
  ];

  return rows.map((row) => row.map(escapeCsvValue).join(",")).join("\r\n");
}
